import type { VariantProps } from 'class-variance-authority';

import { INPUT_STATE_DEFAULT, INPUT_STATE_DISABLED, INPUT_STATE_ERROR, INPUT_STATE_VALID } from '~kui/constants/kui';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

// Shared input state variant definitions with primary and secondary styles
const inputStateVariants = cva('border-2', {
  variants: {
    inputState: {
      [INPUT_STATE_ERROR]:
        'border-destructive focus:outline-destructive focus-visible:outline-destructive focus:outline focus:outline-2 focus-visible:outline',
      [INPUT_STATE_VALID]:
        'border-success-foreground focus:outline-success-foreground focus-visible:outline-success-foreground focus:outline focus:outline-2 focus-visible:outline',
      [INPUT_STATE_DEFAULT]:
        'border-input focus:outline-foreground focus-visible:outline-foreground focus:border-transparent focus:outline focus:outline-2 focus-visible:outline',
      [INPUT_STATE_DISABLED]: 'border-border bg-muted/50 text-muted-foreground cursor-not-allowed',
    },
    variant: {
      primary: '',
      secondary: '',
    },
  },
  compoundVariants: [
    // Secondary variant overrides
    {
      variant: 'secondary',
      inputState: INPUT_STATE_VALID,
      class: 'border-success-foreground',
    },
    {
      variant: 'secondary',
      inputState: INPUT_STATE_DEFAULT,
      class: 'border-input',
    },
    {
      variant: 'secondary',
      inputState: INPUT_STATE_DISABLED,
      class: 'border-input bg-muted text-muted-foreground cursor-not-allowed',
    },
  ],
});

export type InputStateVariants = VariantProps<typeof inputStateVariants>;

interface UseInputStateOptions {
  disabled?: boolean | Ref<boolean>;
  errorMessages?: string[] | Ref<string[]>;
  value?: Ref<unknown>;
  variant?: 'primary' | 'secondary';
}

interface UseInputStateReturn {
  hasError: ComputedRef<boolean>;
  hasValue: ComputedRef<boolean>;
  inputState: ComputedRef<string>;
  getInputStateClasses: (baseClasses?: string, additionalVariants?: object) => ComputedRef<string>;
}

/**
 * Composable for managing input state and associated styles
 * Provides consistent state management and styling across all input components
 */
export const useInputState = (options: UseInputStateOptions = {}): UseInputStateReturn => {
  const { disabled = false, errorMessages = [], value = null, variant = 'primary' } = options;

  // Computed state checks
  const hasError = computed<boolean>(() => {
    const messages = unref(errorMessages);
    return Array.isArray(messages) ? messages.length > 0 : false;
  });

  const hasValue = computed(() => {
    const val = unref(value);
    // Handle different value types
    if (val === null || val === undefined) {
      return false;
    }
    if (typeof val === 'string') {
      return val !== '';
    }
    if (typeof val === 'number') {
      return true;
    }
    if (Array.isArray(val)) {
      return val.length > 0;
    }

    return !!val;
  });

  // Determine current input state
  const inputState = computed(() => {
    if (unref(disabled)) {
      return INPUT_STATE_DISABLED;
    }
    if (hasError.value) {
      return INPUT_STATE_ERROR;
    }
    if (hasValue.value) {
      return INPUT_STATE_VALID;
    }
    return INPUT_STATE_DEFAULT;
  });

  // Generate classes based on state and variants
  const getInputStateClasses = (baseClasses: string = '', additionalVariants: object = {}): ComputedRef<string> => {
    return computed(() => {
      return twMerge(
        baseClasses,
        inputStateVariants({
          inputState: inputState.value,
          variant: unref(variant),
          ...additionalVariants,
        }),
      );
    });
  };

  return {
    hasError,
    hasValue,
    inputState,
    getInputStateClasses,
  };
};

export type InputTypeState = 'valid' | 'error' | undefined;

export interface UseInputStateV2Options {
  disabled?: boolean | Ref<boolean>;
  state?: InputTypeState | Ref<InputTypeState>;
  wrapperClasses?: string[];
  elementClasses?: string[];
}

/**
 * Composable for managing V2 input state styles.
 * V2 inputs use an explicit state prop rather than deriving state from value/errors.
 * Returns wrapperClasses (for the outer border div) and fontClasses (for the inner input).
 */
export const getInputStateClassesV2 = (options: UseInputStateV2Options = {}) => {
  const { disabled = false, state = undefined } = options;

  const wrapperCva = cva('flex h-14 w-full items-center rounded-lg border bg-white transition-all duration-200 border-input focus-within:border-foreground', {
    variants: {
      state: {
        valid: 'border-success-foreground group-hover/kui-input:border-success-foreground focus-within:border-success-foreground',
        error: 'border-destructive group-hover/kui-input:border-destructive focus-within:border-destructive',
      },
      disabled: {
        true: 'border-border bg-muted cursor-not-allowed',
        false: '',
      },
    },
    defaultVariants: {
      disabled: false,
    },
  });

  const fontCva = cva('text-foreground', {
    variants: {
      state: {
        valid: 'placeholder:text-success-foreground text-success-foreground group-hover/kui-input:text-success-foreground focus:text-success-foreground',
        error: 'placeholder:text-destructive text-destructive group-hover/kui-input:text-destructive focus:text-destructive',
      },
      disabled: {
        true: 'text-muted-foreground',
        false: '',
      },
    },
    defaultVariants: {
      disabled: false,
    },
  });

  const elementClasses = computed<string>(() => twMerge('h-full w-full border-none rounded-lg bg-transparent outline-none px-4 py-3', options.elementClasses))

  const wrapperClasses = computed<string>(() =>
    twMerge(wrapperCva({ state: unref(disabled) ? null : unref(state), disabled: unref(disabled) }), options.wrapperClasses),
  );

  const fontClasses = computed<string>(() =>
    twMerge(fontCva({ state: unref(disabled) ? null : unref(state), disabled: unref(disabled) })),
  );

  return { wrapperClasses, elementClasses, fontClasses };
};

/**
 * Composable for character count functionality
 * Used by textarea and other text inputs with character limits
 */
export const useCharacterCount = (value: ComputedRef<string | undefined> | string, maxLength: number) => {
  const characterCount = computed(() => {
    const val = typeof value === 'string' ? value : value.value;
    return val ? val.length : 0;
  });

  const characterCountClass = computed(() => {
    const percentage = (characterCount.value / maxLength) * 100;
    if (percentage >= 90) {
      return 'text-destructive';
    }
    if (percentage >= 75) {
      return 'text-warning-foreground';
    }
    return 'text-muted-foreground';
  });

  const enforceCharacterLimit = (val: string): string => {
    if (val && val.length > maxLength) {
      return val.substring(0, maxLength);
    }
    return val;
  };

  return {
    characterCount,
    characterCountClass,
    enforceCharacterLimit,
  };
};
